import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { IonCol, IonRow, IonCard, IonCardTitle, IonButton, IonCardSubtitle, IonCardHeader, IonCardContent, IonIcon } from "@ionic/react";
import { trash } from "ionicons/icons";
import EventInfoSection from "../Entry/EntryForm/EventInfoSection";
import { Event, EventEntry } from "../../models";
import { ExpandedCardStates } from "../Entry/NewEntry/newEntryTypes"; // Shared type — do not import from the route page.
import EntryCardChip, { ChipTitleOption } from "../Chips/EntryCardChip";
import EntryCard from "../Entry/EntryForm/EntryCard";
import StablingAndExtrasSection, { StablingAndExtrasSectionHandle } from "../Entry/EntryForm/StablingAndExtrasSection";
import { CreateEntryInterface } from "../../utilities/eventEntry/CreateEventEntryUtils";
import ErrorAlert from "../Errors/ErrorAlert";
import { FormattedEntryData, getFormattedEntryData } from "../../utilities/stablingRequest/StablingAndExtrasUtil";
import { PersonContext } from "../../context/PersonContext";
import WaiversSection, { WaiversSectionHandle } from "../Entry/EntryForm/WaiversSection";
import EntrySummarySection, { EntrySummaryNavigateTarget } from "../Entry/EntryForm/EntrySummarySection";
import { FormattedWaiverData, getChipTitle, getFormattedWaiverData } from "../../utilities/eventRules/WaiversUtils";
import { formatStartEndDatesStrings } from "../../utilities/dates/FormatDates";
import EventSummaryCard from "../Entry/EntryForm/EntrySummary/EventSummaryCard";
import { FormattedEventClassData, getFormattedEventClassData } from "../../utilities/eventClass/EventClassUtils";

/** Leading emoji square matches section icon tokens in variables.css / cards.css. */
const EntrySectionLeadingEmojiSquare: React.FC<{ emoji: string; children: React.ReactNode }> = ({ emoji, children }) => {
    return (
        <div className="entry-section-leading">
            <div className="entry-section-leading__icon" aria-hidden="true">
                <span className="entry-section-leading__icon-emoji">{emoji}</span>
            </div>
            {/* Title column: width used to detect whether horse-name words fit; allow wrap, never clip glyphs */}
            <div data-entry-title-column className="entry-section-leading__title-col">
                {children}
            </div>
        </div>
    );
};

interface EntrySectionCardProps {
    event: Event;
    miniTitle?: string;
    title: string;
    subtitle?: string;
    chipTitle: ChipTitleOption;
    trashIcon: boolean;
    expandButtonTitle: string;
    isExpand: boolean;
    cardContent: ExpandedCardStates;
    currentEventEntry?: EventEntry;
    completedEventEntries?: EventEntry[];
    toggleExpansion: (isExpand: boolean) => void;
    onSubmit: (entryInfo?: CreateEntryInterface) => void;
    onDelete?: () => void;
    isSaving?: boolean;
    isDeleting?: boolean;
    /** Stretch card to fill a compact multi-entry grid cell height. */
    fillAvailableHeight?: boolean;
    /** Break long single-word horse names only when even a full-width card can't fit them. */
    allowBreakLongWords?: boolean;
    /** Emoji in the same rounded square as Entry Summary subsection headers (`SubSectionCard`). */
    leadingEmoji?: string;
    /** When set on the Entry Summary card, edit icons jump to the matching form section. */
    onNavigateFromSummary?: (target: EntrySummaryNavigateTarget) => void;
    waiversAccordionFocusKey?: number;
    waiversExpandOnlyEntryId?: string;
    onWaiversCardCollapse?: () => void;
    stablingAccordionFocusKey?: number;
    stablingExpandOnlyEntryId?: string;
    onStablingCardCollapse?: () => void;
    /** Incremented by the parent page when an external control should run this card's primary action. */
    saveRequestKey?: number;
    onUnsavedChangesChange?: (hasUnsavedChanges: boolean) => void;
}

const EntrySectionCard: React.FC<EntrySectionCardProps> = ({
    event,
    miniTitle = "",
    title,
    subtitle,
    chipTitle,
    trashIcon,
    expandButtonTitle,
    isExpand,
    cardContent,
    currentEventEntry,
    completedEventEntries,
    toggleExpansion,
    onSubmit,
    onDelete,
    isSaving = false,
    isDeleting = false,
    fillAvailableHeight = false,
    allowBreakLongWords = false,
    onNavigateFromSummary,
    waiversAccordionFocusKey = 0,
    waiversExpandOnlyEntryId,
    onWaiversCardCollapse,
    stablingAccordionFocusKey = 0,
    stablingExpandOnlyEntryId,
    onStablingCardCollapse,
    saveRequestKey = 0,
    onUnsavedChangesChange,
    leadingEmoji,
}) => {
    const user = useContext(PersonContext);
    const [summaryView, setSummaryView] = useState<"totals" | "byEntry">("byEntry");

    const [entryInfo, setEntryInfo] = useState<CreateEntryInterface | null | undefined>();
    const [error, setError] = useState<string>("");
    const [isEntryInfoValid, setIsEntryInfoValid] = useState<boolean>(false);

    /**
     * True while the card's primary button is waiting on a child-section async save (Stabling/Waivers).
     * Used to disable the button so a second click can't double-fire the save or collapse mid-flight.
     */
    const [isProcessingPrimaryAction, setIsProcessingPrimaryAction] = useState<boolean>(false);

    // Cached Class Fees data
    const [classFeesItems, setClassFeesItems] = useState<FormattedEventClassData[] | null | undefined>(null);
    const [isLoadingClassFees, setIsLoadingClassFees] = useState<boolean>(false);
    
    // Cached Stabling + Extras data (single source of truth — chip status is derived from this).
    const [stablingAndExtrasItems, setStablingAndExtrasItems] = useState<FormattedEntryData[] | null | undefined>(null);
    const [isLoadingStablingData, setIsLoadingStablingData] = useState<boolean>(false);
    const [isLoadingStablingSection, setIsLoadingStablingSection] = useState<boolean>(false);
    const isLoadingStablingChip = isLoadingStablingData || isLoadingStablingSection;

    // Cached Waivers data (single source of truth — chip status is derived from this).
    const [waiversItems, setWaiversItems] = useState<FormattedWaiverData[] | null | undefined>(null);
    const [isLoadingWaiversChip, setIsLoadingWaiversChip] = useState<boolean>(false);

    /**
     * Derive the "any items selected?" flag from the cached `stablingItems` rather than tracking
     * it as separate boolean state. This is the consistent pattern Waivers already uses (chip is
     * computed from `waiversItems`). It also eliminates a class of flicker bugs where the child
     * notified the parent of "no items" during an in-flight remount/reload before the cached
     * data had been re-applied.
     */
    const hasStablingItems = useMemo(
        () => (stablingAndExtrasItems ?? []).some((entry) => entry.rows && entry.rows.length > 0),
        [stablingAndExtrasItems]
    );

    /**
     * Imperative handles to child sections that own their own save logic (Stabling, Waivers).
     * The card's Save button awaits `save()` here before signaling the parent page to collapse,
     * which keeps all sections on a single uniform `onClick → (optional save) → onSubmit` flow.
     */
    const stablingSectionRef = useRef<StablingAndExtrasSectionHandle>(null);
    const waiversSectionRef = useRef<WaiversSectionHandle>(null);
    const lastSaveRequestKeyRef = useRef(saveRequestKey);
    const primaryActionHandlerRef = useRef<() => Promise<void>>(async () => {});

    const setCardUnsavedChanges = useCallback((hasUnsavedChanges: boolean) => {
        onUnsavedChangesChange?.(hasUnsavedChanges);
    }, [onUnsavedChangesChange]);

    const getStablingandExtrasData = async () => {
        setIsLoadingStablingSection(true);
        if (completedEventEntries && completedEventEntries.length > 0) {
            const results = await getFormattedEntryData(event, user, completedEventEntries);
            setStablingAndExtrasItems(results ?? []);
        } else {
            setStablingAndExtrasItems([]);
        }
        setIsLoadingStablingSection(false);
    };

    const getWaiversData = async () => {
        setIsLoadingWaiversChip(true);
        if (completedEventEntries && completedEventEntries.length > 0) {
            const results = await getFormattedWaiverData(event, completedEventEntries);
            setWaiversItems(results ?? []);
        } else {
            setWaiversItems([]);
        }
        setIsLoadingWaiversChip(false);
    };

    const entryIdsKey = useMemo(
        () =>
            (completedEventEntries ?? [])
                .map((e) => e.id)
                .filter(Boolean)
                .sort()
                .join(","),
        [completedEventEntries]
    );

    /** Refetch class fees when entries are saved, not only when entry IDs change. */
    const classFeesRefreshKey = useMemo(
        () =>
            (completedEventEntries ?? [])
                .map((e) => `${e.id}:${e.updatedOn}:${(e.divisionList ?? []).filter(Boolean).join("|")}`)
                .sort()
                .join(","),
        [completedEventEntries]
    );

    useEffect(() => {
        if (!completedEventEntries?.length) {
            setStablingAndExtrasItems(null);
            setWaiversItems(null);
            setClassFeesItems([]);
            setIsLoadingClassFees(false);
            return;
        }
        getStablingandExtrasData();
        getWaiversData();
    }, [entryIdsKey]);

    useEffect(() => {
        if (!completedEventEntries?.length) {
            setClassFeesItems([]);
            setIsLoadingClassFees(false);
            return;
        }
        let cancelled = false;
        (async () => {
            setIsLoadingClassFees(true);
            try {
                const results = await getFormattedEventClassData(event, completedEventEntries);
                if (!cancelled) setClassFeesItems(results ?? []);
            } catch (error) {
                console.error("Could not load class fee totals:", error);
                if (!cancelled) setClassFeesItems((prev) => prev ?? []);
            } finally {
                if (!cancelled) setIsLoadingClassFees(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [classFeesRefreshKey, event, completedEventEntries]);

    /** Refresh aggregates when opening Entry Summary so stabling/waiver edits from other cards are reflected. */
    useEffect(() => {
        if (!isExpand || cardContent !== "summary" || !user || !completedEventEntries?.length) return;

        let cancelled = false;
        (async () => {
            const [stabling, waivers] = await Promise.all([getFormattedEntryData(event, user, completedEventEntries), getFormattedWaiverData(event, completedEventEntries)]);
            if (cancelled) return;
            setStablingAndExtrasItems(stabling ?? []);
            setWaiversItems(waivers ?? []);
            try {
                const classFees = await getFormattedEventClassData(event, completedEventEntries);
                if (cancelled) return;
                setClassFeesItems(classFees ?? []);
            } catch (error) {
                console.error("Could not refresh class fee totals:", error);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isExpand, cardContent, event, user, completedEventEntries]);

    useEffect(() => {
        if (cardContent === "waivers" && !isExpand) {
            onWaiversCardCollapse?.();
        }
    }, [cardContent, isExpand, onWaiversCardCollapse]);

    useEffect(() => {
        if (cardContent === "stabling" && !isExpand) {
            onStablingCardCollapse?.();
        }
    }, [cardContent, isExpand, onStablingCardCollapse]);

    useEffect(() => {
        if (!isExpand) setCardUnsavedChanges(false);
    }, [isExpand, setCardUnsavedChanges]);

    const handleSaveAsIncomplete = () => {
        // Pass a flag indicating this is an incomplete save
        if (entryInfo) {
            onSubmit({ ...entryInfo, isIncomplete: true });
        } else {
            onSubmit({ isIncomplete: true } as CreateEntryInterface);
        }
    };

    /**
     * Submit the entry sub-form. Defaults to "save as incomplete" when the form is invalid
     * so the user can come back and finish later.
     */
    const submitEntryForm = () => {
        if (!isEntryInfoValid) handleSaveAsIncomplete();
        else onSubmit(entryInfo || undefined);
    };

    /**
     * Single uniform Save/Close button flow for every section type:
     *
     *   1. (Optional) Run any child-section async persistence via its imperative `save()` handle.
     *   2. Notify the page (`onSubmit`) so it can collapse the card / advance state.
     *
     * Sections that own their own backend writes (Stabling, Waivers) expose `save()` via a ref.
     * Sections that don't (Header, Summary, Review) skip step 1. The Entry sub-form does its
     * own validation and routes the result through `onSubmit` exactly like the others.
     *
     * The previous `continueClicks` counter pattern (parent increments → child `useEffect`
     * treats any change as a save signal → child calls `onContinueComplete`) was the source
     * of a stale-state bug: when the card re-opened after a prior save, the remounted child
     * saw the still-elevated counter and immediately collapsed the section. Driving saves
     * imperatively makes the flow explicit and removes that whole class of bug.
     */
    const handleContinueClick = async () => {
        if (isProcessingPrimaryAction) return;

        try {
            setIsProcessingPrimaryAction(true);

            if (cardContent === "stabling") {
                const savePromise = stablingSectionRef.current?.save();
                toggleExpansion(false);
                await savePromise;
                setCardUnsavedChanges(false);
                onSubmit(undefined);
                return;
            }

            if (cardContent === "waivers") {
                const savePromise = waiversSectionRef.current?.save();
                toggleExpansion(false);
                await savePromise;
                setCardUnsavedChanges(false);
                onSubmit(undefined);
                return;
            }

            if (cardContent === "header" || cardContent === "summary" || cardContent === "review") {
                setCardUnsavedChanges(false);
                onSubmit(undefined);
                return;
            }

            // Entry sub-form: `cardContent` is "entry" or `entry${n}` (handled identically).
            submitEntryForm();
            setCardUnsavedChanges(false);
        } finally {
            setIsProcessingPrimaryAction(false);
        }
    };

    useEffect(() => {
        primaryActionHandlerRef.current = handleContinueClick;
    });

    useEffect(() => {
        if (saveRequestKey === lastSaveRequestKeyRef.current) return;
        lastSaveRequestKeyRef.current = saveRequestKey;
        if (isExpand) primaryActionHandlerRef.current();
    }, [saveRequestKey, isExpand]);

    const handleDeleteClick = () => {
        if (onDelete) {
            onDelete();
        }
    };

    const summaryDateRange = event.startDate && event.endDate ? formatStartEndDatesStrings(event.startDate, event.endDate, "MMM D, YYYY") : (event.startDate ?? "—");
    const showMiniTitleFirst = chipTitle === "Event Info" && Boolean(miniTitle);
    const isEntryParentCard = leadingEmoji === "🐴" && typeof cardContent === "string" && cardContent.includes("entry");
    const isShowingSavingState = isSaving || isProcessingPrimaryAction;
    const isShowingStatusState = isDeleting || isShowingSavingState;
    const statusButtonText = isDeleting
        ? "DELETING THIS ENTRY"
        : isEntryParentCard
          ? "SAVING ENTRY DATA"
          : "SAVING CHANGES";
    const currentChipTitle: ChipTitleOption = isDeleting
        ? "Deleting"
        : isShowingSavingState
          ? "Saving"
          : isLoadingWaiversChip || isLoadingStablingChip
            ? "Loading"
            : cardContent === "waivers"
              ? getChipTitle(waiversItems || [])
              : chipTitle;
    const showEntryNumberRow = !isEntryParentCard || currentChipTitle === "Not Started";
    const effectiveChipTitle: ChipTitleOption = cardContent === "stabling" ? (hasStablingItems ? "Item(s) Selected" : "Empty") : currentChipTitle;

    const collapsedButtonTitle = () => {
        if (chipTitle === "Event Info") return "VIEW";
        if (cardContent === "summary") return expandButtonTitle;
        if (effectiveChipTitle === "Not Started" || effectiveChipTitle === "Empty") return "Start";
        if (effectiveChipTitle === "Incomplete" || effectiveChipTitle === "Completed" || effectiveChipTitle === "Item(s) Selected") return "Edit";
        return "SAVE";
    };
    const collapsedCtaLabel = collapsedButtonTitle();
    const collapsedCtaColor = collapsedCtaLabel === "Edit" || collapsedCtaLabel === "VIEW" ? "medium" : undefined;

    return (
        // Card's Outer Container
        <IonRow className={`p-0${fillAvailableHeight ? " entry-section-card--fill" : ""}`}>
            <IonCol size="12" className={`p-0 m-0${fillAvailableHeight ? " entry-section-card--fill" : ""}`}>
                <IonCard
                    mode="md"
                    className={[
                        "rounded-3 bg-light",
                        fillAvailableHeight ? "mx-1 m-0 entry-section-card__surface--fill" : "mt-2 mb-0 mx-1",
                        isEntryParentCard && fillAvailableHeight ? "entry-section-card__surface--clip" : "",
                        isEntryParentCard && !fillAvailableHeight ? "entry-section-card__surface--visible" : "",
                    ]
                        .filter(Boolean)
                        .join(" ")}>
                    {" "}
                    {/* IMPORTANT!! Use this IonCard to alter parent card widths */}
                    {/* Card Header */}
                    <IonCardHeader className="px-2 pt-2 pb-0">
                        {leadingEmoji ? (
                            <IonRow className="ion-align-items-start ion-justify-content-between">
                                <IonCol className="pt-0 entry-section-card__min-col">
                                    <EntrySectionLeadingEmojiSquare emoji={leadingEmoji}>
                                        {isEntryParentCard ? (
                                            <>
                                                {showEntryNumberRow ? (
                                                    <IonCardSubtitle className="display-4 text-sans-serif font-weight-normal text-info lh-120 m-0 pt-0">{title}</IonCardSubtitle>
                                                ) : null}
                                                <IonCardTitle className="m-0 p-0 entry-section-card__horse-title-wrap">
                                                    {/*
                                                      Multi-word names may wrap at spaces.
                                                      Single words must stay unbroken — the entry grid reduces columns if a word won't fit.
                                                    */}
                                                    <span
                                                        data-entry-horse-title
                                                        className={`display-3 text-monospace font-weight-700 text-primary lh-110 entry-section-card__horse-title${
                                                            allowBreakLongWords ? " entry-section-card__horse-title--break-long" : ""
                                                        }`}>
                                                        {miniTitle || "New Horse"}
                                                    </span>
                                                </IonCardTitle>
                                                {subtitle ? (
                                                    <IonCardSubtitle className="display-4 text-sans-serif font-weight-bold text-primary lh-120 m-0 pt-0">
                                                        {subtitle}
                                                    </IonCardSubtitle>
                                                ) : null}
                                            </>
                                        ) : (
                                            <>
                                                {showMiniTitleFirst && miniTitle ? (
                                                    <IonCardSubtitle className="display-4 text-sans-serif font-weight-bold text-primary lh-120 m-0 pt-0">
                                                        {miniTitle}
                                                    </IonCardSubtitle>
                                                ) : null}
                                                <IonCardTitle className="display-3 text-monospace font-weight-700 text-primary lh-110 ion-text-wrap m-0">{title}</IonCardTitle>
                                                {!showMiniTitleFirst && miniTitle ? (
                                                    <IonCardSubtitle className="display-4 text-sans-serif font-weight-bold text-primary lh-120 m-0 pt-0">
                                                        {miniTitle}
                                                    </IonCardSubtitle>
                                                ) : null}
                                                {subtitle ? (
                                                    <IonCardSubtitle
                                                        className={`display-4 text-sans-serif font-weight-bold text-primary lh-120 m-0 ${miniTitle ? "pt-1" : "pt-0"}`}>
                                                        {subtitle}
                                                    </IonCardSubtitle>
                                                ) : null}
                                            </>
                                        )}
                                    </EntrySectionLeadingEmojiSquare>
                                </IonCol>
                                {trashIcon ? (
                                    <IonCol size="auto" className="ion-justify-content-center ion-align-items-start d-flex pt-0">
                                        <IonIcon
                                            className="card-icon-size-1 text-danger entry-section-card__trash"
                                            icon={trash}
                                            onClick={() => handleDeleteClick()}
                                        />
                                    </IonCol>
                                ) : null}
                            </IonRow>
                        ) : (
                            <IonRow className="ion-justify-content-between">
                                <IonCol className="pt-0">
                                    {showMiniTitleFirst && miniTitle && (
                                        <IonCardSubtitle className="display-4 text-sans-serif font-weight-bold text-primary lh-120 m-0 pt-0">{miniTitle}</IonCardSubtitle>
                                    )}
                                    <IonCardTitle className="display-3 text-monospace font-weight-700 text-primary lh-110 ion-text-wrap m-0">{title}</IonCardTitle>
                                    {!showMiniTitleFirst && miniTitle && (
                                        <IonCardSubtitle className="display-4 text-sans-serif font-weight-bold text-primary lh-120 m-0 pt-0">{miniTitle}</IonCardSubtitle>
                                    )}
                                    <IonCardSubtitle className={`display-4 text-sans-serif font-weight-bold text-primary lh-120 m-0 ${miniTitle ? "pt-1" : "pt-0"}`}>
                                        {subtitle}
                                    </IonCardSubtitle>
                                </IonCol>
                                {trashIcon && (
                                    <IonCol size="auto" className="ion-justify-content-center ion-align-items-center d-flex">
                                        <IonIcon
                                            className={`card-icon-size-1 text-danger entry-section-card__trash${
                                                isDeleting ? " entry-section-card__trash--disabled" : ""
                                            }`}
                                            icon={trash}
                                            onClick={() => handleDeleteClick()}
                                        />
                                    </IonCol>
                                )}
                            </IonRow>
                        )}
                        <IonRow className="ion-align-items-center ion-justify-content-between pb-2 pt-2 entry-section-card__chip-row">
                            <IonCol size="auto" className="pt-0 entry-section-card__min-col">
                                <EntryCardChip
                                    chipTitle={currentChipTitle}
                                    timestamp={currentEventEntry?.createdOn || undefined}
                                    isRequired={cardContent !== "stabling" || isLoadingStablingChip}
                                    hasItems={cardContent === "stabling" ? hasStablingItems : undefined}
                                />
                            </IonCol>
                        </IonRow>
                    </IonCardHeader>
                    {/* Button to Expand Card */}
                    <IonCardContent
                        className="px-0 pt-0 pb-2"
                        style={fillAvailableHeight ? { flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-end" } : undefined}>
                        {isShowingStatusState ? (
                            // Saving / Deleting State - Show status button (card is collapsed)
                            <IonRow className="ion-justify-content-center">
                                <IonCol sizeXs="12" sizeMd={fillAvailableHeight ? "12" : "6"} className="ion-text-center py-0 px-2">
                                    <IonButton
                                        expand="block"
                                        className="display-4 saving-blink-animation"
                                        color={isDeleting ? "danger" : "medium"}
                                        disabled={true}>
                                        <p className="font-weight-bold m-0">{statusButtonText}</p>
                                    </IonButton>
                                </IonCol>
                            </IonRow>
                        ) : !isExpand ? (
                            // Card Start Button - is hidden when card is expanded
                            <>
                                {cardContent === "summary" && (
                                    <IonRow className="ion-justify-content-center">
                                        <IonCol sizeXs="12" sizeMd="12" className="px-2 py-0 mx-3">
                                            <EventSummaryCard
                                                eventName={event.name}
                                                dateRange={
                                                    event.startDate && event.endDate
                                                        ? formatStartEndDatesStrings(event.startDate, event.endDate, "MMM D, YYYY")
                                                        : (event.startDate ?? "—")
                                                }
                                                onClick={() => onNavigateFromSummary?.({ section: "header" })}
                                            />
                                        </IonCol>
                                    </IonRow>
                                )}
                                <IonRow className="ion-justify-content-center">
                                    {/* Minified entry cards: always full-width CTA (same as xs). Other parent cards keep md=8. */}
                                    <IonCol sizeXs="12" sizeMd={fillAvailableHeight ? "12" : "8"} className="ion-text-center py-0 px-2">
                                        <IonButton expand="block" className="display-4" color={collapsedCtaColor} onClick={() => toggleExpansion(true)}>
                                            {collapsedCtaLabel}
                                        </IonButton>
                                    </IonCol>
                                </IonRow>
                            </>
                        ) : (
                            <IonRow className="ion-justify-content-center">
                                {/* Expanded card body */}
                                <IonCol sizeXs="12" sizeMd="12" className="px-2 py-0 mx-3">
                                    {cardContent === "header" && <EventInfoSection event={event} />}
                                    {cardContent?.includes("entry") && (
                                        <EntryCard
                                            event={event}
                                            currentEventEntry={currentEventEntry}
                                            onSelect={(entryInfo: CreateEntryInterface) => setEntryInfo(entryInfo)}
                                            onValidationChange={(isValid: boolean) => setIsEntryInfoValid(isValid)}
                                            onDirtyChange={setCardUnsavedChanges}
                                        />
                                    )}
                                    {cardContent === "stabling" && (
                                        <StablingAndExtrasSection
                                            ref={stablingSectionRef}
                                            event={event}
                                            completedEventEntries={completedEventEntries}
                                            completedStablingData={stablingAndExtrasItems || undefined}
                                            onItemsChange={(updatedData: FormattedEntryData[]) => {
                                                setStablingAndExtrasItems(updatedData);
                                            }}
                                            onDirtyChange={setCardUnsavedChanges}
                                            onLoadingChange={setIsLoadingStablingSection}
                                            stablingAccordionFocusKey={stablingAccordionFocusKey}
                                            expandStablingOnlyForEventEntryId={stablingExpandOnlyEntryId}
                                        />
                                    )}
                                    {cardContent === "waivers" && (
                                        <WaiversSection
                                            ref={waiversSectionRef}
                                            event={event}
                                            completedEventEntries={completedEventEntries}
                                            waiversData={waiversItems || undefined}
                                            onItemsChange={(updatedWaiversData: FormattedWaiverData[]) => {
                                                setWaiversItems(updatedWaiversData);
                                            }}
                                            onDirtyChange={setCardUnsavedChanges}
                                            waiverAccordionFocusKey={waiversAccordionFocusKey}
                                            expandWaiverOnlyForEventEntryId={waiversExpandOnlyEntryId}
                                        />
                                    )}
                                    {cardContent === "summary" && (
                                        <EntrySummarySection
                                            event={event}
                                            completedEventEntries={completedEventEntries}
                                            formattedClassFeesData={classFeesItems}
                                            formattedStablingData={stablingAndExtrasItems}
                                            waiversData={waiversItems}
                                            waiversLoading={isLoadingWaiversChip}
                                            classFeesLoading={isLoadingClassFees}
                                            onNavigateToSection={onNavigateFromSummary}
                                            summaryView={summaryView}
                                            onSummaryViewChange={setSummaryView}
                                        />
                                    )}
                                    {/* Add more card content conditions as needed */}
                                    {error && <ErrorAlert width="12" error={error} />}
                                    <IonRow className="ion-justify-content-center">
                                        <IonCol sizeXs="11" sizeMd="4" className="p-0">
                                            <IonButton
                                                expand="block"
                                                className="display-4 text-bold"
                                                disabled={isProcessingPrimaryAction}
                                                onClick={() => handleContinueClick()}>
                                                {isProcessingPrimaryAction
                                                    ? "SAVING..."
                                                    : chipTitle === "Event Info"
                                                      ? "CLOSE"
                                                      : cardContent === "summary"
                                                        ? "Submit"
                                                        : "SAVE"}
                                            </IonButton>
                                        </IonCol>
                                    </IonRow>
                                </IonCol>
                            </IonRow>
                        )}
                    </IonCardContent>
                </IonCard>
            </IonCol>
        </IonRow>
    );
};

export default EntrySectionCard;
